import { cn } from "@/lib/utils";
import { hubAvatarColor, hubInitials } from "@/lib/hub/hubIdentity";
import { useHubAvatarUrl } from "@/lib/hub/useHubAvatarUrl";

/**
 * LR HUB avatar — the ONLY avatar used on /social.
 *
 * Default is a deterministic generated-initials avatar. A photo is rendered
 * only when the caller passes an APPROVED storage path (pending/rejected
 * uploads must never be passed here for other members).
 *
 * Do NOT use in LR Direct (blind-bid) views.
 */

const SIZES = {
  xs: "h-7 w-7 text-[10px]",
  sm: "h-8 w-8 text-[10px]",
  md: "h-10 w-10 text-[11px]",
  lg: "h-16 w-16 text-lg",
  xl: "h-24 w-24 text-2xl",
} as const;

export function HubAvatar({
  username,
  avatarPath,
  size = "md",
  className,
}: {
  /** Hub username (or display name) used for initials + color. */
  username?: string | null;
  /** APPROVED `hub-avatars` storage path, else null for initials. */
  avatarPath?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const { url } = useHubAvatarUrl(avatarPath);
  const initials = hubInitials(username);

  if (url) {
    return (
      <img
        src={url}
        alt={username || "Member"}
        loading="lazy"
        className={cn("flex-shrink-0 rounded-full object-cover", SIZES[size], className)}
      />
    );
  }

  return (
    <div
      aria-label={username || "Member"}
      style={{ backgroundColor: hubAvatarColor(username) }}
      className={cn(
        "flex flex-shrink-0 select-none items-center justify-center rounded-full font-bold text-white",
        SIZES[size],
        className,
      )}
    >
      {initials}
    </div>
  );
}

export default HubAvatar;
